import React, { useEffect, useState } from 'react'
import { wtApiCall } from '../../helpers/commons/ApiWrapper'
import './ShareWithClient.css'

// 0712 345 678 -> 254712345678 (Kenya); +44... -> 44...
const waPhone = (raw) => {
  let d = String(raw || '').replace(/\D/g, '')
  if (!d) return ''
  if (d.startsWith('0')) d = '254' + d.slice(1)
  else if (d.length === 9 && /^[17]/.test(d)) d = '254' + d
  return d
}

const copyText = (text) => {
  if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text)
  return new Promise((resolve) => {
    const t = document.createElement('textarea')
    t.value = text
    t.style.position = 'fixed'
    t.style.opacity = '0'
    document.body.appendChild(t)
    t.select()
    try { document.execCommand('copy') } catch (e) {}
    document.body.removeChild(t)
    resolve()
  })
}

const errText = (err) =>
  err?.message || err?.data?.message || err?.response?.data?.message || 'Something went wrong. Please try again.'



// inModal = used inside the "Share listing with client" popup on the Properties list
export default function ShareWithClient({ id, inModal = false, onClose }) {
  const [info, setInfo] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [open, setOpen] = useState(inModal)
  const [clientId, setClientId] = useState('')
  const [busy, setBusy] = useState(false)
  const [current, setCurrent] = useState(null)
  const [copied, setCopied] = useState(false)
  const [showAll, setShowAll] = useState(false)

  const load = () => {
    wtApiCall(
      'property/' + id + '/client-links',
      {},
      'get',
      (res) => { setInfo(res?.data || null); setLoaded(true) },
      () => { setInfo(null); setLoaded(true) },
    )
  }

  useEffect(() => {
    if (id) load()
  }, [id])

  if (!loaded) return inModal ? <div className="swc-sub">Loading…</div> : null
  if (!info || !info.can_share) {
    return inModal ? (
      <div className="swc-sub">
        This listing is not currently available for sharing. Only active listings can be shared with clients.
      </div>
    ) : null
  }

  const links = info.links || []
  const clients = info.clients || []

  const messageFor = (link) => {
    const greeting = link.client_first ? `Dear ${link.client_first},` : 'Good day,'
    const signOff = info.agent_name ? `\n\nKind regards,\n${info.agent_name}` : '\n\nKind regards'
    return (
      `${greeting}\n\nPlease find below a property that may suit your requirements:\n` +
      `${info.title}\n${link.url}\n\n` +
      `This link is valid until ${link.valid_until}. Please let me know if you would like to arrange a viewing.` +
      signOff
    )
  }

  const waHref = (link) =>
    'https://wa.me/' + waPhone(link.client_phone) + '?text=' + encodeURIComponent(messageFor(link))

  const mailHref = (link) =>
    'mailto:' + (link.client_email || '') +
    '?subject=' + encodeURIComponent('Property recommendation: ' + info.title) +
    '&body=' + encodeURIComponent(messageFor(link))

  const create = () => {
    setBusy(true)
    wtApiCall(
      'property/' + id + '/client-links',
      clientId ? { client_id: clientId } : {},
      'post',
      (res) => {
        setBusy(false)
        setCopied(false)
        setCurrent(res?.data?.link || null)
        load()
      },
      (err) => {
        setBusy(false)
        alert(errText(err))
      },
    )
  }

  const finish = () => {
    if (inModal && onClose) return onClose()
    setCurrent(null)
    setOpen(false)
    setClientId('')
    setCopied(false)
  }

  return (
    <div className={'swc-card' + (inModal ? ' in-modal' : '')}>
      <div className="swc-head">
        <div>
          {!inModal && <div className="swc-title">Share with client</div>}
          {inModal && <div className="swc-title-ref">{info.title}</div>}
          <div className="swc-sub">
            Your client will receive a secure link to the listing details and photos.
          </div>
        </div>
        {!open && (
          <button className="swc-btn gold" onClick={() => { setOpen(true); setCurrent(null) }}>
            Share with client
          </button>
        )}
      </div>

      {open && !current && (
        <div className="swc-box">
          <label className="swc-label">
            Select client <span>(optional)</span>
          </label>
          <select className="swc-select" value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">Share without selecting a client</option>
            {clients.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <div className="swc-row">
            <button className="swc-btn green" disabled={busy} onClick={create}>
              {busy ? 'Generating…' : 'Generate link'}
            </button>
            <button className="swc-btn plain" onClick={finish}>Cancel</button>
          </div>
        </div>
      )}

      {current && (
        <div className="swc-box">
          <div className="swc-label">
            Shareable link{current.client_name ? ' for ' + current.client_name : ''}
          </div>
          <input className="swc-url" readOnly value={current.url} onFocus={(e) => e.target.select()} />
          <div className="swc-row">
            <a className="swc-btn wa" href={waHref(current)} target="_blank" rel="noopener noreferrer">
              Send via WhatsApp
            </a>
            <a className="swc-btn green" href={mailHref(current)}>Send via email</a>
            <button className="swc-btn plain" onClick={() => copyText(current.url).then(() => setCopied(true))}>
              {copied ? '✓ Link copied' : 'Copy link'}
            </button>
          </div>
          <div className="swc-note">This link expires on {current.valid_until}.</div>
          <button className="swc-done" onClick={finish}>Close</button>
        </div>
      )}

      {links.length > 0 && (
        <div className="swc-list">
          <div className="swc-list-title">Links you have shared (visible only to you)</div>
          {(showAll ? links : links.slice(0, 3)).map((l) => (
            <div key={l.id} className={'swc-item' + (l.expired ? ' expired' : '')}>
              <div>
                <div className="swc-item-name">{l.client_name || 'General link'}</div>
                <div className="swc-item-meta">
                              {l.expired ? 'Expired' : 'Expires ' + l.valid_until}
                </div>
              </div>
              {!l.expired && (
                <button
                  className="swc-btn small plain"
                  onClick={() => { setCurrent(l); setOpen(true); setCopied(false) }}
                >
                  Share again
                </button>
              )}
            </div>
          ))}
          {links.length > 3 && (
            <button type="button" className="swc-more" onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Show fewer' : `Show all (${links.length})`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}